import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { Box } from '@lifeomic/chroma-react/components/Box';
import { Text } from '@lifeomic/chroma-react/components/Text';
import type {
  DocumentExtractionV2Invocation,
  DocumentExtractionV2Output,
  DocumentExtractionV2Status,
} from '@lifeomic/react-client';
import { useActiveProjectId } from '@lifeomic/phc-web-toolkit/src/hooks';
import { useApiClient } from '@lifeomic/phc-web-toolkit/src/hooks/useRestAPIHooks';
import AuditMasterDetail from '../../AIRules/components/AuditMasterDetail';
import { formatDateTime } from '../../AIRules/utils';
import { memberDetailMessages } from './messages';

const IN_FLIGHT_STATUSES: DocumentExtractionV2Status[] = [
  'scheduled',
  'pending',
  'processing',
  'waiting',
];

const NO_FAILURE_REASON_MESSAGE =
  'No failure reason was recorded for this task.';

type ExtendedDocumentExtractionV2Output = DocumentExtractionV2Output & {
  source_resource_type?: string | null;
  source_resource_id?: string | null;
};

type ExtendedDocumentExtractionV2Invocation = DocumentExtractionV2Invocation & {
  source_resource_type?: string | null;
  source_resource_id?: string | null;
  output?: ExtendedDocumentExtractionV2Output | null;
};

type SourceResource = {
  resourceType: string;
  id: string;
};

export const isDocumentExtractionV2InFlight = (
  task: DocumentExtractionV2Invocation,
) => IN_FLIGHT_STATUSES.includes(task.status);

export const formatDocumentExtractionV2Status = (
  status: DocumentExtractionV2Status,
) => status.charAt(0).toUpperCase() + status.slice(1);

const getTaskSourceResource = (
  task: DocumentExtractionV2Invocation,
): SourceResource | null => {
  const extendedTask = task as ExtendedDocumentExtractionV2Invocation;
  const sourceResourceType =
    extendedTask.source_resource_type ??
    extendedTask.output?.source_resource_type;
  const sourceResourceId =
    extendedTask.source_resource_id ?? extendedTask.output?.source_resource_id;

  if (sourceResourceType && sourceResourceId) {
    return {
      resourceType: sourceResourceType,
      id: sourceResourceId,
    };
  }

  const sourceDocumentReferenceId =
    task.source_document_reference_id ??
    task.output?.source_document_reference_id;

  if (!sourceDocumentReferenceId) {
    return null;
  }

  return {
    resourceType: 'DocumentReference',
    id: sourceDocumentReferenceId.replace(/^DocumentReference\//, ''),
  };
};

const getSourceLabel = (
  task: DocumentExtractionV2Invocation,
  highlightedSourceDocumentReferenceId: string | undefined,
  thisDocumentLabel: string,
) => {
  const sourceResource = getTaskSourceResource(task);

  if (!sourceResource) {
    return '';
  }

  if (
    sourceResource.resourceType === 'DocumentReference' &&
    sourceResource.id === highlightedSourceDocumentReferenceId
  ) {
    return thisDocumentLabel;
  }

  if (sourceResource.resourceType === 'DocumentReference') {
    return sourceResource.id;
  }

  return `${sourceResource.resourceType}/${sourceResource.id}`;
};

const getFailureReason = (task: DocumentExtractionV2Invocation) => {
  const failureReason = (
    task.output as { failure_reason?: string | null } | null | undefined
  )?.failure_reason?.trim();

  return failureReason || NO_FAILURE_REASON_MESSAGE;
};

type DocumentExtractionV2TasksProps = {
  subjectId: string;
  highlightedSourceDocumentReferenceId?: string;
};

type DocumentExtractionV2TasksState = {
  tasks: DocumentExtractionV2Invocation[];
  selectedTaskId: string | null;
};

export const DocumentExtractionV2Tasks: React.FC<
  DocumentExtractionV2TasksProps
> = ({ subjectId, highlightedSourceDocumentReferenceId }) => {
  const { formatMessage } = useIntl();
  const apiClient = useApiClient();
  const projectId = useActiveProjectId();
  const [{ tasks, selectedTaskId }, setTaskState] =
    useState<DocumentExtractionV2TasksState>({
      tasks: [],
      selectedTaskId: null,
    });
  const [isLoadingTasks, setIsLoadingTasks] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const setSelectedTaskId = useCallback((nextSelectedTaskId: string | null) => {
    setTaskState(current => ({
      ...current,
      selectedTaskId: nextSelectedTaskId,
    }));
  }, []);

  const listColumns = useMemo(
    () => [
      {
        header: { label: 'Extraction Task' },
        cell: {
          content: (task: DocumentExtractionV2Invocation) => (
            <Box direction="column">
              <Box direction="row" justify="space-between" gap={1}>
                <Text size="table">{task.task_id}</Text>
                <Text
                  size="table"
                  color="default"
                  style={{ whiteSpace: 'nowrap' }}
                >
                  {formatDocumentExtractionV2Status(task.status)}
                </Text>
              </Box>
              <Text size="table" color="default">
                {formatDateTime(task.created)}
              </Text>
            </Box>
          ),
        },
      },
    ],
    [],
  );

  const loadTasks = useCallback(async () => {
    if (!subjectId || !projectId) {
      return;
    }
    setIsLoadingTasks(true);
    try {
      const response = await apiClient.request(
        'GET /v1/agents-v2/document-extraction-v2/tasks',
        {
          subject_id: subjectId,
          project_id: projectId,
          limit: 25,
        },
      );
      const nextTasks = response.data.tasks;
      setTaskState(current => {
        if (
          current.selectedTaskId &&
          nextTasks.some(task => task.task_id === current.selectedTaskId)
        ) {
          return {
            tasks: nextTasks,
            selectedTaskId: current.selectedTaskId,
          };
        }
        const sourceTask = highlightedSourceDocumentReferenceId
          ? nextTasks.find(task => {
              const sourceResource = getTaskSourceResource(task);
              return (
                sourceResource?.resourceType === 'DocumentReference' &&
                sourceResource.id === highlightedSourceDocumentReferenceId
              );
            })
          : undefined;
        return {
          tasks: nextTasks,
          selectedTaskId: sourceTask?.task_id ?? nextTasks[0]?.task_id ?? null,
        };
      });
      setErrorMessage(null);
    } catch (error) {
      setErrorMessage(
        formatMessage(memberDetailMessages.memberDocumentsExtractionLoadError),
      );
    } finally {
      setIsLoadingTasks(false);
    }
  }, [
    apiClient,
    formatMessage,
    highlightedSourceDocumentReferenceId,
    projectId,
    subjectId,
  ]);

  useEffect(() => {
    loadTasks();
  }, [loadTasks]);

  useEffect(() => {
    if (!tasks.some(isDocumentExtractionV2InFlight)) {
      return;
    }
    const interval = window.setInterval(loadTasks, 3000);
    return () => window.clearInterval(interval);
  }, [loadTasks, tasks]);

  return (
    <Box direction="column" gap={1} style={{ height: '100%', minHeight: 0 }}>
      {errorMessage ? <Text color="negative">{errorMessage}</Text> : null}
      <AuditMasterDetail
        rows={tasks}
        loading={isLoadingTasks && tasks.length === 0}
        emptyMessage={formatMessage(
          memberDetailMessages.memberDocumentsExtractionNoTasks,
        )}
        noSelectionMessage={formatMessage(
          memberDetailMessages.memberDocumentsExtractionSelectTask,
        )}
        rowId={task => task.task_id}
        listColumns={listColumns}
        detailTitle={task => task.task_id}
        detailRows={task => {
          const output: DocumentExtractionV2Output | null | undefined =
            task.output;
          const sourceLabel = getSourceLabel(
            task,
            highlightedSourceDocumentReferenceId,
            formatMessage(
              memberDetailMessages.memberDocumentsExtractionThisDocument,
            ),
          );

          return [
            {
              label: 'Task ID',
              value: task.task_id,
            },
            {
              label: 'Status',
              value: formatDocumentExtractionV2Status(task.status),
            },
            ...(task.status === 'failed'
              ? [
                  {
                    label: 'Failure Reason',
                    value: getFailureReason(task),
                  },
                ]
              : []),
            {
              label: 'Created',
              value: formatDateTime(task.created),
            },
            {
              label: 'Source',
              value: sourceLabel,
            },
            {
              label: 'AI Summary Available',
              value: output?.summary_available ? 'Yes' : 'No',
            },
            {
              label: 'Pages Reviewed',
              value: String(output?.num_pages ?? 0),
            },
            {
              label: 'Entry Coverage',
              value: `entries: ${output?.processed_entries ?? 0}/${
                output?.total_entries ?? 0
              } processed, ${output?.skipped_entries ?? 0} skipped`,
            },
            {
              label: 'Page Coverage',
              value: `pages: ${output?.processed_pages ?? 0}/${
                output?.total_pages ?? 0
              } processed, ${output?.skipped_pages ?? 0} skipped`,
            },
            {
              label: 'Batch Coverage',
              value: `${output?.completed_chunk_count ?? 0}/${
                output?.batch_count ?? 0
              } chunks completed, ${output?.failed_chunk_count ?? 0} failed`,
            },
            {
              label: 'Partial or Truncated',
              value:
                output?.partial || output?.truncated
                  ? `Yes${output?.partial ? ' (partial)' : ''}${
                      output?.truncated ? ' (truncated)' : ''
                    }`
                  : 'No',
            },
            {
              label: 'Candidate Facts',
              value: String(output?.candidate_fact_count ?? 0),
            },
            {
              label: 'Errors',
              value: String(output?.error_count ?? 0),
            },
            {
              label: 'Structured Results',
              value: String(output?.fhir_projection_count ?? 0),
            },
            {
              label: 'Projection Status',
              value: output?.fhir_projection_status ?? 'not-started',
            },
            {
              label: 'Persisted FHIR Artifacts',
              value: String(output?.fhir_artifact_count ?? 0),
            },
            {
              label: 'Artifact Version',
              value: output?.artifact_version ?? 'pending',
            },
          ];
        }}
        selectedRowId={selectedTaskId}
        setSelectedRowId={setSelectedTaskId}
      />
    </Box>
  );
};
